import { isAxiosError } from 'axios';
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';

export interface PharmaCompanySearchRequest {
  pharmaCompanyNameSearchKey?: string;
}

export interface PharmaCompanyInfoResponse {
  pharmaCompanyId: number;
  pharmaCompanyName: string;
  minPrescriptionAmount: number;
  filterInfo: string;
  contractStatus: string;
  contractStartDate: string;
  contractEndDate: string;
}

export interface PharmaCompanyDetailResponse {
  pharmaCompanyId: number;
  pharmaCompanyName: string;
  contractStatus: string;
  contractStartDate: string;
  contractEndDate: string;
  filterInfo: string;
  minPrescriptionAmount: number;
  businessLicenseFile: string;
  businessLicenseNumber: string;
  representativeName: string;
  address: string;
  mainManagerName: string;
  mainManagerPhone: string;
  mainManagerEmail: string;
  mainManagerFaxNumber: string;
  hospitalManagerName: string;
  hospitalManagerPhone: string;
  hospitalManagerEmail: string;
  hospitalManagerKakao: string;
  inventoryManagerName: string;
  inventoryManagerPhone: string;
  inventoryManagerEmail: string;
  inventoryManagerKakao: string;
  settlementManagerName: string;
  settlementManagerPhone: string;
  settlementManagerEmail: string;
  settlementManagerKakao: string;
  taxInvoiceManagerName: string;
  taxInvoiceManagerPhone: string;
  taxInvoiceManagerEmail: string;
  taxInvoiceManagerKakao: string;
  prescriptionDataRegisteredAt: string;
  settlementCompletedAt: string;
  taxInvoiceIssueStandardDate: string;
  taxInvoiceIssueMethod: string;
  commissionPaymentDate: string;
  commissionVerificationMethod: string;
  expenseReportBaseDate: string;
  settlementDataEntry: string;
  originalSubmission: boolean;
  originalSubmissionMethod: string;
  remarks: string;
}

export interface PharmaCompanyCreateRequest {
  pharmaCompanyName: string;
  contractStatus?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  filterInfo?: string;
  minPrescriptionAmount?: number;
  businessLicenseFile?: string;
  businessLicenseNumber: string;
  representativeName: string;
  address?: string;
  mainManagerName?: string;
  mainManagerPhone: string;
  mainManagerEmail?: string;
  mainManagerFaxNumber?: string;
  hospitalManagerName?: string;
  hospitalManagerPhone?: string;
  hospitalManagerEmail?: string;
  hospitalManagerKakao?: string;
  inventoryManagerName?: string;
  inventoryManagerPhone?: string;
  inventoryManagerEmail?: string;
  inventoryManagerKakao?: string;
  settlementManagerName?: string;
  settlementManagerPhone?: string;
  settlementManagerEmail?: string;
  settlementManagerKakao?: string;
  taxInvoiceManagerName?: string;
  taxInvoiceManagerPhone?: string;
  taxInvoiceManagerEmail?: string;
  taxInvoiceManagerKakao?: string;
  prescriptionDataRegisteredAt?: string;
  settlementCompletedAt?: string;
  taxInvoiceIssueStandardDate?: string;
  taxInvoiceIssueMethod?: string;
  commissionPaymentDate?: string;
  commissionVerificationMethod?: string;
  expenseReportBaseDate?: string;
  settlementDataEntry?: string;
  originalSubmission?: boolean;
  originalSubmissionMethod?: string;
  remarks?: string;
}

export type PharmaCompanyUpdateRequest = Partial<PharmaCompanyCreateRequest>;

/** GET /admin/pharma-company/export 응답 */
export interface PharmaCompanyExportResponse {
  fileName: string;
  downloadUrl: string;
  expiresAt: string;
  rowCount: number;
}

export const PHARMA_COMPANY_EXPORT_EMPTY_CODE = 'PHARMA_COMPANY_EXPORT_EMPTY';

/** 전체 건수 동기 생성이라 기본 10초보다 길게 잡는다 */
export const PHARMA_COMPANY_EXPORT_TIMEOUT_MS = 900_000;

export const buildPharmaCompanySearchParams = (
  searchRequest: PharmaCompanySearchRequest,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (searchRequest.pharmaCompanyNameSearchKey) {
    params.pharmaCompanyNameSearchKey =
      searchRequest.pharmaCompanyNameSearchKey;
  }
  return params;
};

/** 조건에 맞는 제약사가 없으면 404 + PHARMA_COMPANY_EXPORT_EMPTY */
export const isPharmaCompanyExportEmptyError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === PHARMA_COMPANY_EXPORT_EMPTY_CODE;
};

export const pharmaService = {
  getPharmaCompanies: async (
    searchRequest: PharmaCompanySearchRequest,
    pageable: Pageable,
  ): Promise<PageResponse<PharmaCompanyInfoResponse>> => {
    const params: Record<string, string | number> = {
      page: pageable.page,
      size: pageable.size,
      ...buildPharmaCompanySearchParams(searchRequest),
    };

    const response = await apiClient.get(createApiPath.pharma.list(), {
      params,
    });
    return response.data;
  },

  /**
   * 제약사 목록 엑셀.
   * 목록과 같은 필터로 서버가 파일을 만든 뒤 서명 URL만 돌려준다.
   */
  exportPharmaCompanies: async (
    searchRequest: PharmaCompanySearchRequest,
  ): Promise<PharmaCompanyExportResponse> => {
    const response = await apiClient.get<PharmaCompanyExportResponse>(
      createApiPath.pharma.export(),
      {
        params: buildPharmaCompanySearchParams(searchRequest),
        timeout: PHARMA_COMPANY_EXPORT_TIMEOUT_MS,
      },
    );
    return response.data;
  },

  getPharmaCompanyDetail: async (
    pharmaCompanyId: number,
  ): Promise<PharmaCompanyDetailResponse> => {
    const response = await apiClient.get(
      createApiPath.pharma.detail(pharmaCompanyId),
    );
    return response.data;
  },

  createPharmaCompany: async (
    createData: PharmaCompanyCreateRequest,
  ): Promise<void> => {
    await apiClient.post(createApiPath.pharma.create(), createData);
  },

  updatePharmaCompany: async (
    pharmaCompanyId: number,
    updateData: PharmaCompanyUpdateRequest,
  ): Promise<void> => {
    await apiClient.put(
      createApiPath.pharma.update(pharmaCompanyId),
      updateData,
    );
  },
};
